import { config } from '@/lib/config';
import { useAuthStore } from '@/lib/store/auth';
/**
 * Single ky instance with auth + tenant headers.
 * Used by TanStack Query hooks.
 *
 * Every call through this instance targets a REST endpoint the live backend
 * hasn't implemented yet (infra / dss / terrain / oi / inventory / weather /
 * agents). In live mode those fail — and NOT only as clean HTTP status errors:
 * the real failure is a CORS-blocked `net::ERR_FAILED` (a `TypeError`), and a
 * same-origin fallback would `.json()`-parse an SPA `index.html` (a
 * `SyntaxError`). ky's `beforeError` hook only sees `HTTPError`, so we wrap the
 * response promise to tag EVERY failure mode as expected — Sentry then drops it
 * (see `api/expectedError` + `lib/sentry.ts`) instead of raising a false alarm.
 *
 * Real integrations use their OWN clients (`custodian.ts`, `partnerships.ts`),
 * so their errors are never tagged and reach Sentry normally.
 */
import ky, { type KyInstance, type Options, type ResponsePromise } from 'ky';
import { markExpectedApiError } from './expectedError';

const instance = ky.create({
  prefixUrl: config.api.base || '/',
  timeout: 10_000,
  retry: { limit: 2, methods: ['get'] },
  hooks: {
    beforeRequest: [
      (req) => {
        const user = useAuthStore.getState().user;
        if (user) {
          // In real mode, a real bearer token would go here.
          req.headers.set('X-Tenant-Id', user.tenantId);
        }
      },
    ],
  },
});

/** Tag any rejection as an expected mock-API failure and rethrow. Covers the
 *  network/CORS (`TypeError`), parse (`SyntaxError`) and `HTTPError` modes. */
function tagged<T>(p: Promise<T>): Promise<T> {
  return p.then(undefined, (err: unknown) => {
    markExpectedApiError(err);
    throw err;
  });
}

const BODY_METHODS = ['json', 'text', 'blob', 'arrayBuffer', 'formData'] as const;

/** Tag every failure mode of a ky ResponsePromise as expected, in place. */
function wrapResponsePromise(rp: ResponsePromise): ResponsePromise {
  // Mark rp-level failures (HTTPError / network TypeError) even when the caller
  // awaits the raw response with no body helper. This observer swallows the
  // rejection so it never becomes a floating unhandled rejection; the caller's
  // own await/body-helper still receives the same (now-marked) error object.
  void rp.then(undefined, markExpectedApiError);
  // Route body helpers through `tagged` so a parse failure (e.g. an SPA
  // index.html served for an unproxied /api path) is marked and rethrown too.
  for (const method of BODY_METHODS) {
    const original = rp[method].bind(rp) as (...a: never[]) => Promise<unknown>;
    rp[method] = ((...args: never[]) => tagged(original(...args))) as never;
  }
  return rp;
}

const REQUEST_METHODS = ['get', 'post', 'put', 'patch', 'delete', 'head'] as const;
type RequestMethod = (typeof REQUEST_METHODS)[number];

export const http = new Proxy(instance, {
  get(target, prop, receiver) {
    if (typeof prop === 'string' && (REQUEST_METHODS as readonly string[]).includes(prop)) {
      return (url: string | URL | Request, options?: Options) =>
        wrapResponsePromise(target[prop as RequestMethod](url, options));
    }
    return Reflect.get(target, prop, receiver);
  },
}) as KyInstance;
